import { Box, Collapse, useTheme } from "@mui/material";
import { CommonProps } from "@mui/material/OverridableComponent";
import React from "react";
import {
  ActionGroup,
  ItemVariant,
  resolveActionGroupPreferences,
  SelectableItem,
  useResolveActionGroupColors,
} from "shared-ts-ui-action";
import {
  BooleanSetting,
  useUserSettingWithDefault,
} from "shared-ts-utils-user-settings";
import { GroupNode } from "./GroupNode";
import { List } from "./List";
import { Layout } from "../../../Layout";
import { LayoutDirection } from "../../../LayoutDirection";

export interface GroupListProps extends CommonProps {
  variant?: ItemVariant;
  layout: Layout;
  layoutDirection?: LayoutDirection;
  item: ActionGroup;
  hideLabels?: boolean;
  labelOrientation?: "horizontal" | "vertical";
  onSelected?: (item: SelectableItem) => void;
}

export const GroupList = (props: GroupListProps) => {
  const { layout, item } = props;
  const variant = props.variant ?? "rounded";
  const theme = useTheme();
  const preferences = resolveActionGroupPreferences(
    props.item.preferences,
    layout,
  );
  const hideLabels = props.hideLabels ?? preferences?.hideLabels ?? false;
  const labelOrientation = props.labelOrientation ?? "horizontal";
  const layoutDirection = props.layoutDirection ?? preferences?.layoutDirection;
  const displayGroupNode = preferences?.displayGroupNode ?? false;
  const [isOpen, setIsOpen] = useUserSettingWithDefault(
    `GroupList[${item?.reference ?? item?.name}].open`,
    BooleanSetting,
    preferences?.displayGroupNode !== true,
    {
      storeLocally: true,
    },
  );

  const actionGroupColors = useResolveActionGroupColors();

  const handleGroupNodeOpenChange = (value: boolean) => {
    setIsOpen(value);
  };

  const handleSelectedItem = (item: SelectableItem) => {
    props.onSelected?.(item);
  };

  if (displayGroupNode) {
    return (
      <Box className={`Item GroupList layout-${layout} variant-${variant}`}>
        <GroupNode
          variant={variant}
          layout={layout}
          item={item}
          hideLabels={hideLabels}
          labelOrientation={labelOrientation}
          open={isOpen}
          onOpenChange={handleGroupNodeOpenChange}
        />
        <Collapse
          className={"GroupList-container"}
          in={isOpen}
          timeout="auto"
          unmountOnExit
          sx={{ mt: 1, pl: 2, pr: 2 }}
        >
          <List
            variant={variant}
            layout={layout}
            layoutDirection={layoutDirection}
            items={props.item.items}
            hideLabels={hideLabels}
            onSelected={handleSelectedItem}
          />
        </Collapse>
      </Box>
    );
  } else {
    return (
      <List
        variant={variant}
        layout={layout}
        layoutDirection={layoutDirection}
        items={props.item.items}
        hideLabels={hideLabels}
        onSelected={handleSelectedItem}
        sx={{
          backgroundColor: actionGroupColors.primary,
          borderStyle: "solid",
          borderWidth: 1,
          borderColor: actionGroupColors.primary,
          borderRadius: "17px",
          padding: 1,
        }}
      />
    );
  }
};
